import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, apiFetch } from '../../config/api';
import { useMessages } from '../../components/Messages';
import './WarehouseDashboard.css';

const WarehouseOverview = forwardRef(({ onNavigate, onViewPickList, onRefresh }, ref) => {
  const { t } = useTranslation('warehouse');
  const { user } = useAuth();
  const { showError } = useMessages();

  const [overviewStats, setOverviewStats] = useState({
    pendingDeliveries: 0,
    qualityInspections: 0,
    pendingPickLists: 0,
    lowStockItems: 0,
    pendingRequisitions: 0,
    pendingReturns: 0,
    tasksRequiringAttention: 0
  });
  const [loadingOverview, setLoadingOverview] = useState(false);
  const [pickLists, setPickLists] = useState([]);

  // Fetch overview statistics for dashboard
  const fetchOverviewStats = async () => {
    try {
      setLoadingOverview(true);

      // Fetch data from multiple endpoints concurrently
      const [
        inventoryStatsResponse,
        pickListsResponse,
        requisitionsResponse,
        purchaseOrdersResponse,
        qualityItemsResponse
      ] = await Promise.all([
        apiFetch(getApiUrl('/api/inventory/stats')),
        apiFetch(getApiUrl('/api/pick-lists?limit=100')),
        apiFetch(getApiUrl('/api/inventory-requisitions?limit=100')),
        apiFetch(getApiUrl('/api/purchase-orders')),
        apiFetch(getApiUrl('/api/grn-items/quality-inspection?limit=100'))
      ]);

      let stats = {
        pendingDeliveries: 0,
        qualityInspections: 0,
        pendingPickLists: 0,
        lowStockItems: 0,
        pendingRequisitions: 0,
        pendingReturns: 0,
        tasksRequiringAttention: 0
      };

      // Process inventory stats
      if (inventoryStatsResponse.ok) {
        const inventoryStats = await inventoryStatsResponse.json();
        stats.lowStockItems = inventoryStats.low_stock || 0;
      }

      // Process pick lists data
      if (pickListsResponse.ok) {
        const pickListsData = await pickListsResponse.json();
        const pickListsList = pickListsData || [];
        stats.pendingPickLists = pickListsList.filter(pl =>
          pl.status === 'PENDING' || pl.status === 'IN_PROGRESS'
        ).length;
        // Also update the pickLists state for the recent pick lists display
        setPickLists(pickListsList);
      }


      // Process requisitions data
      if (requisitionsResponse.ok) {
        const requisitionsData = await requisitionsResponse.json();
        const requisitions = requisitionsData || [];
        stats.pendingRequisitions = requisitions.filter(req =>
          req.status === 'PENDING' || req.status === 'APPROVED'
        ).length;
      }

      // Process purchase orders data
      if (purchaseOrdersResponse.ok) {
        const poData = await purchaseOrdersResponse.json();
        const purchaseOrders = poData || [];
        // Same POs as Receiving Operations → Incoming Deliveries
        stats.pendingDeliveries = purchaseOrders.filter(po =>
          ['SENT', 'CONFIRMED', 'PARTIALLY_RECEIVED'].includes(po.status)
        ).length;
      }

      // Process quality inspections data
      if (qualityItemsResponse.ok) {
        const qualityData = await qualityItemsResponse.json();
        const qualityItems = qualityData || [];
        stats.qualityInspections = qualityItems.length;
      }

      // Calculate total tasks requiring attention
      stats.tasksRequiringAttention =
        stats.pendingDeliveries +
        stats.qualityInspections +
        stats.pendingPickLists +
        stats.lowStockItems +
        stats.pendingRequisitions;

      setOverviewStats(stats);
    } catch (error) {
      console.error('Error fetching overview stats:', error);
    } finally {
      setLoadingOverview(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchOverviewStats();
  }, []);

  // Expose refresh method to parent via ref
  useImperativeHandle(ref, () => ({
    refresh: fetchOverviewStats
  }));

  // Helper to navigate to a specific tab/subtab in the parent
  const navigateTo = (tab, subTab, options) => {
    if (onNavigate) {
      onNavigate(tab, subTab, options);
    }
  };

  // Helper to view a pick list detail
  const handleViewPickList = (pickList) => {
    if (onViewPickList) {
      onViewPickList(pickList);
    }
  };

  return (
    <div className="warehouse-overview">

      {loadingOverview ? (
        <div className="loading-state">{t('dashboard.messages.loadingStatistics')}...</div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="warehouse-stats-grid">
            <div className="warehouse-stat-card urgent" onClick={() => {
              const el = document.getElementById('priority-tasks-section');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}>
              <div className="warehouse-stat-icon">⚠️</div>
              <div className="warehouse-stat-content">
                <h3>{t('dashboard.cards.tasksRequiringAttention')}</h3>
                <p className="warehouse-stat-value">{overviewStats.tasksRequiringAttention}</p>
                <p className="warehouse-stat-description">{t('dashboard.labels.totalUrgentItems')}</p>
              </div>
            </div>

            <div className="warehouse-stat-card pending" onClick={() => {
              navigateTo('receiving-ops', 'incoming');
            }}>
              <div className="warehouse-stat-icon">📦</div>
              <div className="warehouse-stat-content">
                <h3>{t('dashboard.cards.pendingDeliveries')}</h3>
                <p className="warehouse-stat-value">{overviewStats.pendingDeliveries}</p>
                <p className="warehouse-stat-description">{t('dashboard.labels.awaitingReceipt')}</p>
              </div>
            </div>

            <div className="warehouse-stat-card warning" onClick={() => {
              navigateTo('receiving-ops', 'quality');
            }}>
              <div className="warehouse-stat-icon">🔍</div>
              <div className="warehouse-stat-content">
                <h3>{t('dashboard.cards.qualityInspections')}</h3>
                <p className="warehouse-stat-value">{overviewStats.qualityInspections}</p>
                <p className="warehouse-stat-description">{t('dashboard.labels.pendingInspection')}</p>
              </div>
            </div>

            <div className="warehouse-stat-card info" onClick={() => {
              navigateTo('shipping-ops', 'picking');
            }}>
              <div className="warehouse-stat-icon">📝</div>
              <div className="warehouse-stat-content">
                <h3>{t('dashboard.cards.pickLists')}</h3>
                <p className="warehouse-stat-value">{overviewStats.pendingPickLists}</p>
                <p className="warehouse-stat-description">{t('dashboard.labels.readyForPicking')}</p>
              </div>
            </div>


            <div className="warehouse-stat-card error" onClick={() => {
              navigateTo('inventory-mgmt', 'inventory', { stockFilter: 'low' });
            }}>
              <div className="warehouse-stat-icon">⚠️</div>
              <div className="warehouse-stat-content">
                <h3>{t('dashboard.cards.lowStockItems')}</h3>
                <p className="warehouse-stat-value">{overviewStats.lowStockItems}</p>
                <p className="warehouse-stat-description">{t('dashboard.labels.belowMinimumLevel')}</p>
              </div>
            </div>

            <div className="warehouse-stat-card pending" onClick={() => {
              navigateTo('returns-reqs', 'requisitions');
            }}>
              <div className="warehouse-stat-icon">📋</div>
              <div className="warehouse-stat-content">
                <h3>{t('dashboard.cards.materialRequisitions')}</h3>
                <p className="warehouse-stat-value">{overviewStats.pendingRequisitions}</p>
                <p className="warehouse-stat-description">{t('dashboard.labels.pendingApproval')}</p>
              </div>
            </div>
          </div>


          {/* Priority Tasks */}
          {overviewStats.tasksRequiringAttention > 0 && (
            <div className="priority-tasks" id="priority-tasks-section">
              <h3>⚡ {t('dashboard.sections.priorityTasks')}</h3>
              <div className="priority-list">
                {overviewStats.pendingDeliveries > 0 && (
                  <div className="priority-item" onClick={() => {
                    navigateTo('receiving-ops', 'incoming');
                  }}>
                    <span className="priority-icon">📦</span>
                    <span className="priority-text">
                      {t('dashboard.priority.deliveriesNeedReceiving', { count: overviewStats.pendingDeliveries })}
                    </span>
                    <span className="priority-arrow">→</span>
                  </div>
                )}

                {overviewStats.qualityInspections > 0 && (
                  <div className="priority-item" onClick={() => {
                    navigateTo('receiving-ops', 'quality');
                  }}>
                    <span className="priority-icon">🔍</span>
                    <span className="priority-text">
                      {t('dashboard.priority.itemsNeedInspection', { count: overviewStats.qualityInspections })}
                    </span>
                    <span className="priority-arrow">→</span>
                  </div>
                )}

                {overviewStats.pendingPickLists > 0 && (
                  <div className="priority-item" onClick={() => {
                    navigateTo('shipping-ops', 'picking');
                  }}>
                    <span className="priority-icon">📝</span>
                    <span className="priority-text">
                      {t('dashboard.priority.pickListsReadyProcessing', { count: overviewStats.pendingPickLists })}
                    </span>
                    <span className="priority-arrow">→</span>
                  </div>
                )}

                {overviewStats.lowStockItems > 0 && (
                  <div className="priority-item" onClick={() => {
                    navigateTo('inventory-mgmt', 'inventory', { stockFilter: 'low' });
                  }}>
                    <span className="priority-icon">⚠️</span>
                    <span className="priority-text">
                      {t('dashboard.priority.itemsBelowMinStock', { count: overviewStats.lowStockItems })}
                    </span>
                    <span className="priority-arrow">→</span>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Pick Lists Card - Similar to Supervisor Dashboard */}
          <div style={{ marginTop: '2rem', display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
            <div style={{
              background: 'var(--bg-secondary)',
              border: '2px solid var(--border)',
              borderRadius: '8px',
              padding: '1.5rem',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '0',
                left: '0',
                right: '0',
                height: '4px',
                background: overviewStats.pendingPickLists > 0 ? '#ff9800' : '#4caf50'
              }}></div>

              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                <span style={{ fontSize: '1.5em', marginRight: '0.5rem' }}>📝</span>
                <h3 style={{ margin: 0, fontSize: '1.1em' }}>{t('dashboard.sections.pickLists')}</h3>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                  <span className={`status-badge ${overviewStats.pendingPickLists > 0 ? 'status-warning' : 'status-success'}`}>
                    {overviewStats.pendingPickLists} {t('dashboard.status.pending')}
                  </span>
                  <span className="status-badge status-secondary">
                    {pickLists.filter(pl => pl.status === 'COMPLETED').length} {t('dashboard.status.today')}
                  </span>
                </div>
              </div>

              {pickLists.filter(pl => pl.status === 'PENDING' || pl.status === 'IN_PROGRESS').slice(0, 3).map(pickList => {
                let priorityClass = 'status-secondary';
                if (pickList.priority === 'HIGH') priorityClass = 'status-warning';
                if (pickList.priority === 'URGENT') priorityClass = 'status-error';

                return (
                  <div key={pickList.pick_list_id} style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    padding: '0.75rem',
                    marginBottom: '0.5rem',
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onClick={() => handleViewPickList(pickList)}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <strong>{pickList.pick_list_number}</strong>
                      <span className={`status-badge ${priorityClass}`}>{pickList.priority}</span>
                    </div>
                    <div style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                      {pickList.po_number || pickList.so_number} • {pickList.customer_name || 'N/A'}
                    </div>
                    <div style={{ fontSize: '0.85em', color: 'var(--text-tertiary)', marginTop: '0.25rem' }}>
                      {t('dashboard.pickLists.byLabel')} {pickList.assigned_to_name || t('dashboard.pickLists.unassigned')} • {pickList.notes || t('dashboard.pickLists.noNotes')}
                    </div>
                  </div>
                );
              })}

              {overviewStats.pendingPickLists === 0 && (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem 1rem',
                  color: 'var(--text-secondary)',
                  fontStyle: 'italic'
                }}>
                  {t('dashboard.pickLists.noPendingPickLists')}
                </div>
              )}

              <button
                className="btn-action btn-primary"
                onClick={() => {
                  navigateTo('shipping-ops', 'picking');
                }}
                style={{ width: '100%', marginTop: '1rem' }}
              >
                {t('dashboard.buttons.viewAllPickLists')} →
              </button>
            </div>
          </div>

          {/* Recent Pick Lists Table */}
          <div className="recent-pick-lists" style={{ marginTop: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3>📝 {t('dashboard.sections.recentPickLists')}</h3>
              <button
                className="btn-link"
                onClick={() => {
                  navigateTo('shipping-ops', 'picking');
                }}
                style={{ color: 'var(--primary)', cursor: 'pointer', background: 'none', border: 'none', fontSize: '0.9em' }}
              >
                {t('dashboard.buttons.viewAll')} →
              </button>
            </div>

            {pickLists.length === 0 ? (
              <div style={{
                padding: '2rem',
                textAlign: 'center',
                background: 'var(--bg-secondary)',
                borderRadius: '8px',
                color: 'var(--text-secondary)'
              }}>
                <p>{t('pickLists.messages.noPickLists')}</p>
              </div>
            ) : (
              <div className="warehouse-table-container" style={{ maxHeight: '300px', overflow: 'auto' }}>
                <table className="warehouse-table">
                  <thead>
                    <tr>
                      <th>{t('pickLists.headers.pickListNumber')}</th>
                      <th>{t('warehouseOverview.columns.order')}</th>
                      <th>{t('pickLists.headers.customer')}</th>
                      <th>{t('common:ui.type')}</th>
                      <th>{t('common:ui.priority')}</th>
                      <th>{t('pickLists.headers.status')}</th>
                      <th>{t('warehouseOverview.columns.assignedTo')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pickLists.slice(0, 5).map(pickList => {
                      let statusClass = 'status-pending';
                      if (pickList.status === 'IN_PROGRESS') statusClass = 'status-in-progress';
                      if (pickList.status === 'COMPLETED') statusClass = 'status-success';
                      if (pickList.status === 'CANCELLED') statusClass = 'status-error';

                      let priorityClass = 'status-secondary';
                      if (pickList.priority === 'HIGH') priorityClass = 'status-warning';
                      if (pickList.priority === 'URGENT') priorityClass = 'status-error';

                      return (
                        <tr key={pickList.pick_list_id} style={{ cursor: 'pointer' }} onClick={() => handleViewPickList(pickList)}>
                          <td><strong>{pickList.pick_list_number}</strong></td>
                          <td>{pickList.po_number || pickList.so_number || '-'}</td>
                          <td>{pickList.customer_name || '-'}</td>
                          <td style={{ fontSize: '0.85em' }}>{pickList.pick_list_type}</td>
                          <td><span className={`status-badge ${priorityClass}`}>{pickList.priority}</span></td>
                          <td><span className={`status-badge ${statusClass}`}>{pickList.status}</span></td>
                          <td>{pickList.assigned_to_name || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </>
      )}
    </div>
  );
});

WarehouseOverview.displayName = 'WarehouseOverview';

export default WarehouseOverview;
